import React, { useEffect, FormEvent } from 'react';
import 'firebase/auth';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import Link from 'next/link';
import { useMachine } from '@xstate/react';
import passwordResetStateMachine from '../../machines/passwordResetStateMachine';
import { ErrorToastComponent } from '@components/web/ErrorToast';
import { useRouter } from 'next/router';
import UserAdmissionBanner from '@components/web/signup-signin/UserAdmissionBanner';
import MimoWebLogo from '@components/icons/mimoWebLogo';
import RequestPasswordResetFormComponent from '@components/web/signup-signin/RequestPasswordResetForm';
import SetNewPasswordFormComponent from '@components/web/signup-signin/SetNewPasswordForm';
import { SpacerComponent } from '@components/Spacer';
import mascotWithOpenDoor from '/public/images/illustrations/mascotWithOpenDoor.png';
import emailCheck from '/public/images/illustrations/email-check.svg';
import password from '/public/images/illustrations/password.svg';
import brokenLink from '/public/images/illustrations/broken-link.svg';
import passwordDone from '/public/images/illustrations/password-done.svg';
import Head from 'next/head';
import { GetStaticProps } from 'next';

const PasswordResetPage = () => {
  const [state, send] = useMachine(passwordResetStateMachine);
  const router = useRouter();

  useEffect(() => {
    const { oobCode, email } = router.query;
    if (router.isReady) {
      if (oobCode) {
        send({ type: 'INITIALISE_FOR_CHANGING_THE_PASSWORD' });
        if (email) {
          send({ type: 'ENTER_EMAIL', email: `${email}` });
        }
        send({ type: 'VERIFY', resetCode: `${oobCode}` });
      } else {
        send({ type: 'INITIALISE_FOR_REQUESTING_EMAIL' });
      }
    }
  }, [router.isReady]);

  useEffect(() => {
    if (state.context.errors.serviceError !== '') {
      toast.dismiss();
      toast.custom((toast) => (
        <ErrorToastComponent toast={toast}>
          {state.context.errors.serviceError}
        </ErrorToastComponent>
      ));
    }
  }, [state.context.errors.serviceError]);

  function emailChange(e: React.ChangeEvent<HTMLInputElement>) {
    send({
      type: 'ENTER_EMAIL',
      email: e.target.value,
    });
  }

  function passwordChange(e: React.ChangeEvent<HTMLInputElement>) {
    send({
      type: 'ENTER_PASSWORD',
      password: e.target.value,
    });
  }

  function submitRequest(e: FormEvent) {
    e.preventDefault();
    send({
      type: 'REQUEST_RESET',
    });
  }

  function submitPassword(e: FormEvent) {
    e.preventDefault();
    send({
      type: 'SUBMIT_PASSWORD',
    });
  }

  async function handleRestart() {
    delete router.query.oobCode;
    delete router.query.email;
    await router.push(router);
    send({ type: 'RESTART' });
  }

  return (
    <>
      <Head>
        <title>
          Reset password for the Mimo app or website. Simple, secure
        </title>
        <meta
          name="description"
          content="Forgot your Mimo password? Reset it quickly and securely on the Mimo password reset page. Regain access to your account to keep learning to code."
          key="description"
        ></meta>
        <meta
          name="og:title"
          content="Reset password for the Mimo app or website. Simple, secure"
          key="og:title"
        ></meta>
        <meta
          name="og:description"
          content="Forgot your Mimo password? Reset it quickly and securely on the Mimo password reset page. Regain access to your account to keep learning to code."
          key="og:description"
        ></meta>
      </Head>
      <Toaster
        toastOptions={{
          duration: Infinity,
        }}
        containerStyle={{ zIndex: 50 }}
        position="top-center"
        reverseOrder={false}
      />
      <div className="flex min-h-screen items-center bg-product-background-primary-light px-6 py-4 font-mimopro dark:bg-product-background-primary-dark">
        <div className="m-auto flex flex-col tablet:w-[500px] tablet:py-10">
          <div className="flex justify-center">
            <Link href="/">
              <MimoWebLogo className="h-6 text-product-text-primary-light dark:text-product-text-primary-dark sm:h-8"></MimoWebLogo>
            </Link>
          </div>
          <SpacerComponent className="h-6 grow sm:h-16" />
          {(state.matches({ requestPasswordResetEmail: 'initial' }) ||
            state.matches({
              requestPasswordResetEmail: 'requestingResetEmail',
            })) && (
            <>
              <UserAdmissionBanner
                text={`Please enter your e-mail address`}
                src={mascotWithOpenDoor}
                alt={`Mimo mascot with open door`}
              ></UserAdmissionBanner>
              <SpacerComponent className="h-7 sm:h-8" />
              <RequestPasswordResetFormComponent
                onChange={emailChange}
                onSubmit={submitRequest}
                sending={state.matches(
                  'requestPasswordResetEmail.requestingResetEmail',
                )}
                error={state.context.errors.email}
              />
            </>
          )}
          {state.matches(
            'requestPasswordResetEmail.passwordResetEmailSent',
          ) && (
            <>
              <UserAdmissionBanner
                text={`Almost done, check your inbox!`}
                src={emailCheck}
                alt={'Mimo mascot and 2 people'}
              ></UserAdmissionBanner>
              <SpacerComponent className="h-7 sm:h-8" />
              <p className="text-center text-sm text-product-text-primary-light dark:text-product-text-primary-dark">
                Confirm your identity by clicking the link we sent to{' '}
                <span className="font-bold text-product-button-primary-enabled-dark text-product-button-primary-enabled-light ">
                  {state.context.email}
                </span>
                .
              </p>
            </>
          )}
          {(state.matches({ changePassword: 'initial' }) ||
            state.matches({ changePassword: 'verifyResetCode' }) ||
            state.matches({ changePassword: 'readyForPasswordInput' }) ||
            state.matches({
              changePassword: 'passwordResetInProgress',
            })) && (
            <>
              <UserAdmissionBanner
                text={
                  <>
                    Enter new password for{' '}
                    <Link
                      href={state.context.email}
                      className="font-bold text-product-button-primary-enabled-light hover:text-product-button-primary-hovered-light
                      dark:text-product-button-primary-enabled-dark dark:hover:text-product-button-primary-hovered-dark"
                    >
                      {state.context.email}
                    </Link>
                    .
                  </>
                }
                src={password}
                alt={'Mimo mascot and 2 people'}
              ></UserAdmissionBanner>
            </>
          )}
          {(state.matches({ changePassword: 'readyForPasswordInput' }) ||
            state.matches({ changePassword: 'passwordResetInProgress' })) && (
            <>
              <SpacerComponent className="h-7 sm:h-8" />
              <SetNewPasswordFormComponent
                onChange={passwordChange}
                onSubmit={submitPassword}
                sending={state.matches(
                  'changePassword.passwordResetInProgress',
                )}
                error={state.context.errors.password}
              />
            </>
          )}
          {state.matches('changePassword.resetCodeExpired') && (
            <>
              <UserAdmissionBanner
                text={'This link is invalid'}
                src={brokenLink}
                alt={'Broken link'}
              ></UserAdmissionBanner>
              <SpacerComponent className="h-7 sm:h-8" />
              <p className="text-center text-sm text-product-text-primary-light dark:text-product-text-primary-dark">
                Your password reset link might have expired. Request a new link
                using the button below
              </p>
              <SpacerComponent className="h-7 sm:h-8" />
              <button
                type="button"
                onClick={() => handleRestart()}
                className="flex w-full justify-center rounded-xl border border-transparent bg-product-button-primary-enabled-light px-4
                py-2 text-base font-semibold text-product-text-reversed-light shadow-sm hover:bg-product-button-primary-hovered-light focus:outline-none dark:bg-product-button-primary-enabled-dark dark:hover:bg-product-button-primary-hovered-dark"
              >
                Request a new password reset link
              </button>
            </>
          )}
          {state.matches('changePassword.passwordResetFinished') && (
            <>
              <UserAdmissionBanner
                text={'Password successfully reset'}
                src={passwordDone}
                alt={'Broken link'}
              ></UserAdmissionBanner>
              <SpacerComponent className="h-7 sm:h-8" />
              <p className="text-center text-sm text-product-text-primary-light dark:text-product-text-primary-dark">
                You can now log in with your new credentials
              </p>
              <SpacerComponent className="h-7 sm:h-8" />
              <button
                type="button"
                onClick={() =>
                  router.push(`/web/login?email=${state.context.email}`)
                }
                className="hidden w-full justify-center rounded-xl border border-transparent bg-product-button-primary-enabled-light px-4 py-2 text-base font-semibold text-product-text-reversed-light shadow-sm hover:bg-product-button-primary-hovered-light focus:outline-none dark:bg-product-button-primary-enabled-dark dark:hover:bg-product-button-primary-hovered-dark lg:flex"
              >
                Continue to Log in
              </button>
            </>
          )}
          <SpacerComponent className="h-6 grow sm:h-16" />
          {!state.matches('changePassword.passwordResetFinished') && (
            <>
              <p className="text-center text-sm text-product-text-primary-light dark:text-product-text-primary-dark">
                <Link
                  href="/web/login"
                  className="font-bold text-product-button-primary-enabled-light hover:text-product-button-primary-hovered-light dark:text-product-button-primary-enabled-dark dark:hover:text-product-button-primary-hovered-dark"
                >
                  Back to Login
                </Link>
              </p>
              <SpacerComponent className="h-6 sm:h-8" />
            </>
          )}
          {!(
            state.matches({ changePassword: 'readyForPasswordInput' }) ||
            state.matches({ changePassword: 'passwordResetFinished' }) ||
            state.matches({ changePassword: 'passwordResetInProgress' })
          ) && (
            <>
              <p className="text-center text-sm text-product-text-primary-light dark:text-product-text-primary-dark">
                If you&apos;re still having trouble, reach out to{' '}
                <Link
                  href="https://support.mimo.org/hc/en-us/requests/new"
                  className="font-bold text-product-button-primary-enabled-light hover:text-product-button-primary-hovered-light dark:text-product-button-primary-enabled-dark dark:hover:text-product-button-primary-hovered-dark"
                >
                  support
                </Link>
                .
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PasswordResetPage;

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {},
  };
};
